import { Activity } from "lucide-react";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { EmptyState } from "@/components/ui/feedback";
import { NotAvailableState } from "@/components/ui/system-state";
import { WIP_ACTION_HINT, WipHint } from "@/components/ui/wip-hint";
import { SectionTitle } from "@/features/oversight/oversight-ui";

/**
 * Provider Health (MON-02, `/monitoring/provider`).
 *
 * Work-in-progress layout port (design-ref 2026-10-03, task
 * r2-monitoring-provider). The uat design is
 * `.design-ref/prototype/features/oversight/monitoring.tsx` →
 * `ProviderHealthDetail`: a breadcrumb, a six-field provider summary panel, a
 * capability matrix, a "Last 10 Probe Results" strip and a link out to the
 * provider's own status page. That structure and its classes are carried over;
 * its data is not.
 *
 * The prototype reads `PROVIDERS[0]` from a static fixture — an uptime, an
 * error rate, p50/p99 latencies, capability flags and probe timings, none of it
 * ever collected. Custodial-provider health has no owning service and no metric
 * collection (`context/page_pipeline/BACKEND_GAPS.md` row 18), so every field
 * renders the shared `UnavailableValue` marker and both lists render a designed
 * empty state. A rendered "Healthy" badge beside a provider name would be a
 * status verdict nothing produced, which is exactly the failure this route's
 * previous defer note pinned.
 *
 * The prototype's status-page link is `href="#"` — there is no provider status
 * URL configured anywhere in this environment, so it is rendered disabled with
 * `WIP_ACTION_HINT` rather than navigating nowhere.
 */

/** Id of the inline explanation the disabled control points at (WIP_ACTION_HINT copy). */
const WIP_HINT_ID = "provider-health-wip-hint";

/** Provider summary fields, in the designer's order. Values are not available. */
const PROVIDER_FIELDS = [
  "Provider",
  "Status",
  "Uptime (30d)",
  "Error Rate",
  "Latency p50",
  "Latency p99",
] as const;

export default function MonitoringProviderPage() {
  return (
    <div>
      <Breadcrumb items={[{ label: "Monitoring", href: "/monitoring" }, { label: "Provider Health" }]} />

      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h1 className="text-[24px] font-bold leading-tight tracking-tight text-ink">Provider Health</h1>
        <WipHint />
      </div>
      <p className="mb-5 max-w-[720px] text-sm leading-relaxed text-muted">
        This area has no backend in this environment yet, so no provider health is shown or estimated.
      </p>
      <p id={WIP_HINT_ID} className="sr-only">
        {WIP_ACTION_HINT}
      </p>

      <NotAvailableState
        title="Provider health is not available yet"
        description="Custody-provider uptime, error rate, and latency are not collected or stored anywhere in this environment."
      />

      <SectionTitle className="mt-6">Provider Summary</SectionTitle>
      <div className="mb-6 grid grid-cols-2 gap-5 rounded-lg border border-line bg-surface px-5 py-4 sm:grid-cols-3">
        {PROVIDER_FIELDS.map((label) => (
          <div key={label}>
            <div className="text-xs text-muted">{label}</div>
            <div className="mt-1 text-[15px] font-bold text-ink">
              <UnavailableValue />
            </div>
          </div>
        ))}
      </div>

      <SectionTitle>Capability Matrix</SectionTitle>
      <EmptyState
        icon={<Activity aria-hidden className="size-5 text-muted" />}
        title="No capability matrix has been reported"
        description="Which signing, custody and chain capabilities the provider supports is not read or stored in this environment, so no capability is listed."
        meta="A capability is only shown once a provider has reported it. Nothing on this page is assumed."
      />

      <SectionTitle className="mt-6">Last 10 Probe Results</SectionTitle>
      <EmptyState
        title="No probe results have been recorded"
        description="Provider probes are not run in this environment, so there is no latency or availability history to show."
      />

      <button
        type="button"
        disabled
        title={WIP_ACTION_HINT}
        aria-describedby={WIP_HINT_ID}
        className="mt-6 text-sm font-medium text-muted disabled:cursor-not-allowed disabled:opacity-60"
      >
        View provider status page
      </button>
    </div>
  );
}
